// The target maximum file size in bytes for resized images.
const MAX_FILE_SIZE_BYTES = 1 * 1024 * 1024; // 1MB

/**
 * Resizes and compresses an image file to be under 1MB.
 * It maintains the aspect ratio and iteratively reduces JPEG quality
 * until the size constraint is met.
 * @param {File} file - The original image file to resize.
 * @returns {Promise<string>} A promise that resolves with the base64 data URL of the resized image.
 */
export const resizeImage = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 1920; // Set a max width for performance and reasonable dimensions.
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while preserving aspect ratio.
        if (width > MAX_WIDTH) {
          height = (height * MAX_WIDTH) / width;
          width = MAX_WIDTH;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return reject(new Error('Could not get canvas context'));
        }
        ctx.drawImage(img, 0, 0, width, height);

        let quality = 0.9; // Start with high quality.

        // A recursive function to try encoding the image at different quality levels.
        const tryEncode = () => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                return reject(new Error('Canvas to Blob conversion failed'));
              }
              // If the blob is small enough or we've reached a minimum quality, resolve.
              if (blob.size <= MAX_FILE_SIZE_BYTES || quality <= 0.1) {
                const dataUrlReader = new FileReader();
                dataUrlReader.onloadend = () => {
                    resolve(dataUrlReader.result as string);
                };
                dataUrlReader.readAsDataURL(blob);
              } else {
                // Otherwise, reduce quality and try again.
                quality -= 0.1;
                tryEncode();
              }
            },
            'image/jpeg',
            quality
          );
        };
        tryEncode();
      };

      img.onerror = (err) => reject(new Error(`Image load error: ${err}`));
      if (event.target?.result) {
        img.src = event.target.result as string;
      } else {
        reject(new Error("FileReader did not return a result."));
      }
    };
    
    reader.onerror = (err) => reject(new Error(`FileReader error: ${err}`));
    reader.readAsDataURL(file);
  });
};
